import React, { useState, useEffect } from 'react';
import { api } from '../../utils/requestInterceptor';
import { EyeIcon, CheckIcon, ClockIcon, PencilIcon, XMarkIcon, SaveIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const ClientPortalProjectPlan = ({ projectId, clientId, token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectPlans, setProjectPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editableFields, setEditableFields] = useState([]);
  const [rowData, setRowData] = useState({});
  const [savingRow, setSavingRow] = useState(false);
  const [addingNewRow, setAddingNewRow] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchProjectPlans();
    }
  }, [clientId]);

  const fetchProjectPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use token-based access or client ID-based access
      const url = token 
        ? `/access/client-project-plans/${encodeURIComponent(token)}`
        : `/clients/${clientId}/project-plans`;
      
      const response = await api.get(url);
      console.log('Project plans response:', response.data);
      
      // Check for both success formats - original client-portal format and admin format
      if (response.data.success && response.data.plans) {
        setProjectPlans(response.data.plans);
      } else if (response.data.plans) {
        // Admin endpoint format might just have plans directly
        setProjectPlans(response.data.plans);
      } else if (Array.isArray(response.data)) {
        // Or it might return an array directly
        setProjectPlans(response.data);
      } else {
        setProjectPlans([]);
      }
    } catch (err) {
      console.error('Error fetching project plans:', err);
      setError('Failed to load project plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanDetails = async (planId) => {
    try {
      setLoading(true);
      
      // Use the admin endpoint pattern with client-portal access
      const response = await api.get(`/clients/${clientId}/project-plans/${planId}`);
      console.log('Plan details response:', response.data);
      
      // Handle different response formats
      let plan = null;
      if (response.data.success && response.data.plan) {
        // Original client-portal format
        plan = response.data.plan;
      } else if (response.data.id) {
        // Direct plan object
        plan = response.data;
      }
      
      if (plan) {
        // Ensure plan has valid arrays for field_definitions and rows
        if (!plan.field_definitions || !Array.isArray(plan.field_definitions)) {
          plan.field_definitions = [];
          console.warn('Plan is missing field_definitions array');
        }
        
        if (!plan.rows || !Array.isArray(plan.rows)) {
          plan.rows = [];
          console.warn('Plan is missing rows array');
        }
        
        // Initialize field_values array for each row if missing
        plan.rows.forEach(row => {
          if (!row.field_values || !Array.isArray(row.field_values)) {
            row.field_values = [];
          }
        });
        
        setSelectedPlan(plan);
        
        // Process field definitions
        if (plan.field_definitions && plan.field_definitions.length > 0) {
          setFieldDefinitions(plan.field_definitions);
          
          // For the proof of concept, make all fields editable
          const allFields = plan.field_definitions
            .map(field => field.field_definition_id);
          
          setEditableFields(allFields);
        } else {
          setFieldDefinitions([]);
          setEditableFields([]);
        }
      } else {
        toast.error('Failed to load plan details');
      }
    } catch (err) {
      console.error('Error fetching plan details:', err);
      toast.error('Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPlan = (planId) => {
    fetchPlanDetails(planId);
  };

  const handleBack = () => {
    setSelectedPlan(null);
    setEditingRow(null);
    setAddingNewRow(false);
    setRowData({});
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Start editing a row
  const handleEditRow = (row) => {
    // Initialize the row data with current field values
    const initialData = {};
    
    row.field_values.forEach(fieldValue => {
      initialData[fieldValue.field_definition_id] = fieldValue.value_text || '';
    });
    
    setRowData(initialData);
    setEditingRow(row.id);
  };

  // Cancel editing a row
  const handleCancelEdit = () => {
    setEditingRow(null);
    setRowData({});
  };

  // Handle field value change
  const handleFieldChange = (fieldId, value) => {
    setRowData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Save row changes
  const handleSaveRow = async (rowId) => {
    try {
      setSavingRow(true);
      
      // Prepare field values from the edited data
      const fieldValues = Object.entries(rowData)
        .map(([fieldId, value]) => ({
          field_definition_id: parseInt(fieldId),
          value_text: value
        }));
      
      // API endpoint for client updates - use the admin endpoint pattern
      const url = `/clients/${clientId}/project-plans/${selectedPlan.id}/rows/${rowId}`;
      console.log('Saving row updates to:', url);
      
      const response = await api.patch(
        url,
        { field_values: fieldValues }
      );
      
      console.log('Row update response:', response.data);
      
      // Handle response - check different success formats
      if (response.data.success || response.data.field_values) {
        toast.success('Changes saved successfully');
        
        // Update the local data
        const updatedPlan = { ...selectedPlan };
        const rowIndex = updatedPlan.rows.findIndex(r => r.id === rowId);
        
        if (rowIndex >= 0) {
          // Update the field values in the row
          fieldValues.forEach(newField => {
            const fieldIndex = updatedPlan.rows[rowIndex].field_values.findIndex(
              fv => fv.field_definition_id === newField.field_definition_id
            );
            
            if (fieldIndex >= 0) {
              updatedPlan.rows[rowIndex].field_values[fieldIndex].value_text = newField.value_text;
            } else {
              updatedPlan.rows[rowIndex].field_values.push(newField);
            }
          });
          
          setSelectedPlan(updatedPlan);
        }
      } else {
        toast.error(response.data.message || 'Failed to save changes');
      }
    } catch (err) {
      console.error('Error saving row updates:', err);
      toast.error('Failed to save changes');
    } finally {
      setSavingRow(false);
      setEditingRow(null);
      setRowData({});
    }
  };

  // Check if a field is editable - now always returns true for proof of concept
  const isFieldEditable = (fieldId) => {
    return true; // All fields are editable for proof of concept
  };

  // Render a field value or edit input
  const renderField = (row, field) => {
    const fieldValue = row.field_values.find(
      fv => fv.field_definition_id === field.field_definition_id
    );
    const value = fieldValue ? fieldValue.value_text : '';
    const isRowCompleted = row.completed || false;
    
    // If we're editing this row
    if (editingRow === row.id) {
      const currentValue = rowData[field.field_definition_id] !== undefined 
        ? rowData[field.field_definition_id] 
        : value;
      
      // Render appropriate input based on field type
      if (field.field_type === 'dropdown') {
        return (
          <select
            value={currentValue}
            onChange={(e) => handleFieldChange(field.field_definition_id, e.target.value)}
            disabled={savingRow}
            className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">-- Select --</option>
            {field.options?.values?.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );
      }
      
      const inputType = 
        field.field_type === 'numeric' ? 'number' : 
        field.field_type === 'date' ? 'date' : 'text';
      
      return (
        <input
          type={inputType}
          value={currentValue}
          onChange={(e) => handleFieldChange(field.field_definition_id, e.target.value)}
          disabled={savingRow}
          className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          placeholder={`Enter ${field.name.toLowerCase()}`}
        />
      );
    }
    
    // Not editing - just show the value
    if (!value || value.trim() === '') {
      return <span className="text-gray-400 italic">-</span>;
    }
    
    // For date fields, format the date nicely
    if (field.field_type === 'date' && value) {
      try {
        return formatDate(value);
      } catch (e) {
        return value;
      }
    }
    
    return <span>{value}</span>;
  };

  // Handle adding a new row
  const handleAddRow = () => {
    // Initialize empty data for all fields
    const initialData = {};
    fieldDefinitions.forEach(field => {
      initialData[field.field_definition_id] = '';
    });
    
    setRowData(initialData);
    setAddingNewRow(true);
  };

  // Save a new row
  const handleSaveNewRow = async () => {
    try {
      setSavingRow(true);
      
      // Create empty field values for all field definitions - matching ProjectPlanDetail.js approach
      const initialFieldValues = fieldDefinitions.map(field => ({
        field_definition_id: field.field_definition_id,
        value_text: rowData[field.field_definition_id] || ''
      }));
      
      // API endpoint for adding a new row
      const url = `/clients/${clientId}/project-plans/${selectedPlan.id}/rows`;
      console.log('Adding new row to:', url);
      
      const response = await api.post(
        url,
        { field_values: initialFieldValues }
      );
      
      console.log('Add row response:', response.data);
      
      if (response.data.success || response.data.row || response.data.id) {
        const newRow = response.data.row || response.data;
        toast.success('New row added successfully');
        
        // Add the new row to the local state
        const updatedPlan = { ...selectedPlan };
        updatedPlan.rows = [...(updatedPlan.rows || []), newRow];
        setSelectedPlan(updatedPlan);
      } else {
        toast.error(response.data.message || 'Failed to add row');
      }
    } catch (err) {
      console.error('Error adding new row:', err);
      toast.error('Failed to add new row');
    } finally {
      setSavingRow(false);
      setAddingNewRow(false);
      setRowData({});
    }
  };

  if (loading && !projectPlans.length && !selectedPlan) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
        <span className="ml-3 text-sm text-gray-500">Loading project plans...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (selectedPlan) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleBack}
              className="flex items-center text-indigo-600 hover:text-indigo-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to plans
            </button>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900">{selectedPlan.name}</h2>
            <p className="text-gray-600 mt-1">{selectedPlan.description}</p>
            <div className="mt-2 text-sm text-gray-500">
              <span className="mr-4">Created: {formatDate(selectedPlan.created_at)}</span>
              {selectedPlan.is_completed && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Completed
                </span>
              )}
              {selectedPlan.project_created && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                  Project Created
                </span>
              )}
            </div>
            
            {/* Client Input Instructions Section */}
            <div className="mb-8 bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 bg-indigo-50">
                <h3 className="text-lg leading-6 font-medium text-indigo-800">Project Plan</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Please review the plan below and provide the requested information.
                </p>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <div className="mt-1 text-sm text-gray-900">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Click the "Edit" button next to any row to modify that row's information.</li>
                    <li>You can add new rows using the "Add Row" button at the top of the table.</li>
                    <li>Fill in the requested information and click "Save" when done.</li>
                  </ul>
                </div>
                
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <InformationCircleIcon className="h-5 w-5 text-indigo-400 mr-2" />
                    <span>Need help? Contact your project manager for assistance.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Project plan rows */}
          <div className="overflow-x-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Project Items</h3>
              <button
                type="button"
                onClick={handleAddRow}
                className="inline-flex items-center rounded-md bg-indigo-100 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-200"
                disabled={loading || addingNewRow || editingRow !== null}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Row
              </button>
            </div>
            
            {/* Form for adding a new row - simplified to match ProjectPlanDetail.js */}
            {addingNewRow && (
              <div className="bg-indigo-50 p-4 mb-6 rounded-md shadow-sm">
                <h4 className="text-lg font-medium text-indigo-800 mb-4">Add New Row</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300 mb-4">
                    <thead className="bg-gray-50">
                      <tr>
                        {fieldDefinitions.map((field) => (
                          <th key={field.field_definition_id} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {field.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        {fieldDefinitions.map((field) => (
                          <td key={field.field_definition_id} className="px-3 py-4 text-sm text-gray-500">
                            {field.field_type === 'dropdown' ? (
                              <select
                                value={rowData[field.field_definition_id] || ''}
                                onChange={(e) => handleFieldChange(field.field_definition_id, e.target.value)}
                                disabled={savingRow}
                                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              >
                                <option value="">-- Select --</option>
                                {field.options?.values?.map((opt, idx) => (
                                  <option key={idx} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : field.field_type === 'date' ? (
                              <input
                                type="date"
                                value={rowData[field.field_definition_id] || ''}
                                onChange={(e) => handleFieldChange(field.field_definition_id, e.target.value)}
                                disabled={savingRow}
                                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              />
                            ) : (
                              <input
                                type={field.field_type === 'numeric' ? 'number' : 'text'}
                                value={rowData[field.field_definition_id] || ''}
                                onChange={(e) => handleFieldChange(field.field_definition_id, e.target.value)}
                                disabled={savingRow}
                                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setAddingNewRow(false);
                      setRowData({});
                    }}
                    disabled={savingRow}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNewRow}
                    disabled={savingRow}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {savingRow ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
            
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  {fieldDefinitions.length > 0 && fieldDefinitions.map((field) => (
                    <th key={field.field_definition_id} className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {field.name}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedPlan.rows && selectedPlan.rows.map((row) => {
                  const isEditing = editingRow === row.id;
                  // All rows have editable fields now
                  const hasEditableFields = true;
                  
                  return (
                    <tr key={row.id} className={row.completed ? 'bg-gray-50' : (isEditing ? 'bg-indigo-50' : '')}>
                      {fieldDefinitions.map((field) => (
                        <td key={`${row.id}-${field.field_definition_id}`} className="px-3 py-4 text-sm text-gray-500">
                          {renderField(row, field)}
                        </td>
                      ))}
                      <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSaveRow(row.id)}
                              disabled={savingRow}
                              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              {savingRow ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={savingRow}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEditRow(row)}
                            disabled={row.completed}
                            className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                              row.completed 
                                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                : 'text-indigo-700 bg-indigo-100 hover:bg-indigo-200'
                            }`}
                            title={row.completed ? 'Cannot edit completed items' : 'Edit this row'}
                          >
                            <PencilIcon className="mr-1 h-4 w-4" />
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Project Plans</h3>
        
        {projectPlans.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <p>No project plans available.</p>
          </div>
        ) : (
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Plan Name</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {projectPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-indigo-600 sm:pl-6">{plan.name}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(plan.created_at)}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        {plan.is_completed ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckIcon className="mr-1 h-4 w-4" />
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <ClockIcon className="mr-1 h-4 w-4" />
                            In Progress
                          </span>
                        )}
                        
                        {plan.project_created && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Project Created
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <button
                        onClick={() => handleViewPlan(plan.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortalProjectPlan; 